import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faTrash, faUserSlash } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { useIsChannelCurator } from '@components/Channels/useIsChannelCurator';
import { DeletePostCommentModal } from '@components/Feed/Post/Comments/DeletePostCommentModal';
import { DropdownMenuItem } from '@components/basics/Dropdowns/GlobalDropdown';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { UserCommentItem } from '@components/main-components/user/UserCommentItem';
import { UserCommentReplies } from '@components/main-components/user/UserCommentReplies';
import { useAuth } from '@context/auth/base';
import { ModalType, useModal } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import {
  CommentAuthorFragmentDoc,
  getFragment,
  PostCommentsRowFragmentDoc,
} from '@graphql/reactQuery';

import { useLexicalValidate } from '@hooks/useLexicalValidate';
import { useStableCallback } from '@hooks/useStableCallback';
import { AdminDeletePostCommentModal } from '../DeletePostModal';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PostCommentsRow on PostComment {
    id
    content
    lexicalContent
    author {
      id
      hasFeedAccess
      ...CommentAuthor
    }
    createdAt
    upvoteCount
    repliesCount
    labels
    currentHolderOfRelease
    post {
      __typename
      id
      channel {
        id
      }
    }
  }
`);

export function PostCommentsRow({
  postCommentsRowFrag,
}: {
  postCommentsRowFrag: FragmentType<PostCommentsRowFragmentDoc>;
}) {
  const comment = getFragment(PostCommentsRowFragmentDoc, postCommentsRowFrag);

  const commentAuthor = getFragment(CommentAuthorFragmentDoc, comment.author);

  const { isChannelCurator, isChannelOwner } = useIsChannelCurator({
    channelId: comment.post.channel?.id,
  });

  const { authUser } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  const { openModal } = useModal();

  const handleDeleteClicked = useStableCallback(() => {
    openModal(ModalType.DELETE_POST_COMMENT_CONFIRMATION, {
      body: <DeletePostCommentModal postCommentId={comment.id} />,
    });
  });

  const handleAdminDeleteClicked = useStableCallback(() => {
    openModal(ModalType.DELETE_POST_COMMENT_CONFIRMATION, {
      body: <AdminDeletePostCommentModal postCommentId={comment.id} />,
    });
  });

  const handleAdminBlockUserClicked = useStableCallback(() => {
    window.open(`${window.location.origin}/admin/moderate/${commentAuthor.id}`, '_blank');
  });

  const showArtistTag = useMemo(
    () => comment.labels.some(label => label === 'RELEASE_ARTIST'),
    [comment.labels],
  );

  const validatedLexicalContent = useLexicalValidate(comment.lexicalContent);

  return (
    <div>
      <UserCommentItem
        postCommentId={comment.id}
        userFragment={comment.author}
        content={validatedLexicalContent || comment.content}
        createdAt={comment.createdAt}
        showVerifiedBadge={!!commentAuthor.artist}
        showArtistTag={showArtistTag}
        selectedSlotInfo={null}
        upvoteCount={comment.upvoteCount}
        showFeedCuratorBadge={comment.author.hasFeedAccess}
        showCollectorTag={comment.currentHolderOfRelease}
        action={
          (authUser?.id === comment.author.id || authUser?.roles?.isAdmin || isChannelCurator) && (
            <div className="flex items-center">
              <ThreeDotsDropdown
                setIsDropdownOpen={setIsDropdownOpen}
                isDropdownOpen={isDropdownOpen}
                dropdownProps={{
                  align: 'end',
                  queue: true,
                }}
              >
                {authUser?.id === comment.author.id && (
                  <DropdownMenuItem onClick={handleDeleteClicked}>
                    <div className="flex items-center gap-3 font-base text-base-m font-medium text-destructive700">
                      <FontAwesomeIcon icon={faTrash} size="lg" />
                      Delete
                    </div>
                  </DropdownMenuItem>
                )}
                {authUser?.roles?.isAdmin ? (
                  <>
                    {authUser?.id !== comment.author.id && (
                      <DropdownMenuItem onClick={handleAdminDeleteClicked}>
                        <div className="flex items-center gap-3 font-base text-base-m font-medium text-destructive700">
                          <FontAwesomeIcon icon={faTrash} size="lg" />
                          Delete (Admin)
                        </div>
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuItem onClick={handleAdminBlockUserClicked}>
                      <div className="flex items-center gap-3 font-base text-base-m font-medium text-destructive700">
                        <FontAwesomeIcon icon={faUserSlash} size="lg" />
                        Block User (Admin)
                      </div>
                    </DropdownMenuItem>
                  </>
                ) : isChannelCurator ? (
                  <DropdownMenuItem onClick={handleDeleteClicked}>
                    <div className="flex items-center gap-3 font-base text-base-m font-medium text-destructive700">
                      <FontAwesomeIcon icon={faTrash} size="lg" />
                      Delete ({isChannelOwner ? 'Owner' : 'Moderator'})
                    </div>
                  </DropdownMenuItem>
                ) : null}
              </ThreeDotsDropdown>
            </div>
          )
        }
      />

      <UserCommentReplies
        repliesCount={comment.repliesCount}
        parentCommentId={comment.id}
        postId={comment.post.id}
      />
    </div>
  );
}
